// Sprint-007 D4 — auth v2 (login/onboarding/reset) governed copy. Same
// governing rule as `lib/copy.ts`/`lib/adminCopy.ts`: every string here is
// centralized so it is never accidentally spelled two different ways
// across `views/LoginView.tsx`/`views/CredentialSetupView.tsx`.
//
// Every literal string in this file is copied VERBATIM from
// `specs/ux-auth-v2-flows.md` §6's "Prioritized copy summary" table
// (SA-reconciled, ADR-002 Amendment 4) and §6.1's admin-set-password table
// (TD-058, ADR-002 Amendment 12) — do not hand-edit a string here
// without updating that table first. A small number of additional strings
// (marked below) cover error states the UX doc's §2.2/§1.4 tables don't
// enumerate (malformed/taken username, generic fallback) — these are
// NOT in the copy table and were written defensively, in the app's
// existing voice, to fill a gap rather than leave an unhandled state
// silently falling through to a raw server error string.

import { firstName } from "./personColor";

/**
 * TD-058 / UX §6.1 row B — the third mode. `"must-change"` is the forced
 * change after an ADMIN-SET password (the admin typed a password and read
 * it to the person); `"reset"` remains the forced change after a recovery
 * MAGIC-LINK consume. Both are driven by the same server flag
 * (`password_reset_required`) and both exit through
 * `POST /auth/password/reset` — they differ only in copy, because the
 * person's situation differs: one just typed a password somebody else chose,
 * the other tapped a link. UX §1's "same component either way — do not build
 * two screens" still governs; this is a third copy mode, not a third screen.
 */
export type CredentialMode = "first-time" | "reset" | "must-change";

// ---------------------------------------------------------------------------
// Returning login (UX §2)
// ---------------------------------------------------------------------------

export const RETURNING_LOGIN_HEADING = "Let's get you back in";
// UX review 2026-10 (fix/ux-signin): was "Your name", which invited people
// to type their display name ("Worker One") when the server wants the
// one-word sign-in name (`USERNAME_RE`). The server now also accepts an
// unambiguous display name, but the label names the real thing.
export const RETURNING_LOGIN_NAME_LABEL = "Sign-in name";
export const RETURNING_LOGIN_PASSWORD_LABEL = "Password";
export const RETURNING_LOGIN_PRIMARY_BUTTON = "Continue";
export const RETURNING_LOGIN_GENERIC_ERROR =
  "That didn't work — try again, or ask your farm admin for a new link.";
export const RETURNING_LOGIN_RATE_LIMIT_ERROR = "Too many tries — wait a minute, then try again.";
export const RETURNING_LOGIN_FOOTER_HINT = "Can't get in? Ask your farm admin for a new link.";
/** UX review 2026-10 — shown UNDER the generic error (never instead of
 * it) when a rejected login's typed name contains a space: the most
 * common miss is typing the display name. Purely client-side, from what
 * the person typed — it reveals nothing about which accounts exist. */
export const RETURNING_LOGIN_SPACE_HINT =
  "Your sign-in name is one word — check with your farm admin if you're unsure.";
export const RETURNING_LOGIN_PASSKEY_BUTTON = "Use Face ID or fingerprint";

// Reused verbatim from `hooks/useMe.ts`'s existing `/me` connectivity-error
// copy (not a new phrase — the app already has one governed string for
// "genuinely can't reach the server," and UX §2.2 explicitly says to reuse
// that distinction rather than invent a third).
export const CONNECTIVITY_ERROR_MESSAGE =
  "Couldn't reach Hayloft. Check your connection and try again.";

// ---------------------------------------------------------------------------
// Onboarding / reset — ONE screen, two copy modes (UX §1: "Same component
// either way — do not build two screens.")
// ---------------------------------------------------------------------------

export function onboardingHeading(mode: CredentialMode): string {
  if (mode === "first-time") return "Let's get you set up";
  if (mode === "must-change") return "Time to pick your own password";
  return "Let's get you back in";
}

// Deliberately the FULL display name, never `firstName()` — UX §1.1's
// "name confirmation line is deliberate and important" safety check exists
// specifically to catch a mis-forwarded link, including the collision
// case where the admin disambiguated two same-first-named helpers by editing
// the DISPLAY name ("Worker One B."). Truncating to a first name here would
// silently defeat that exact safety check for the audience it protects.
export function onboardingSubheading(mode: CredentialMode, displayName: string): string {
  if (mode === "first-time") return `Setting up Hayloft for ${displayName}.`;
  // UX §6.1 row B is explicit that `"must-change"` keeps the FULL display
  // name too, "and it matters MORE here: a spoken password can reach the
  // wrong kid on a shared phone far more easily than a tapped link."
  if (mode === "must-change") return `Picking a new password for ${displayName}.`;
  return `Resetting Hayloft for ${displayName}.`;
}

export const ONBOARDING_NOT_YOU_LINE = "Not you? Ask your farm admin for the right link.";

// UX review 2026-10 (fix/ux-signin) — same relabel as
// `RETURNING_LOGIN_NAME_LABEL`: this is the sign-in handle, not the display
// name. The helper line now echoes the ACTUAL value (normalized the way the
// server will store it) so there is no doubt what to type next time:
// "You'll sign in as **worker**." `USERNAME_HELPER_TEXT` stays as the
// empty-field fallback.
export const USERNAME_FIELD_LABEL = "Sign-in name";
export const USERNAME_HELPER_TEXT = "This is what you'll type to sign in next time.";
export const USERNAME_HELPER_SIGN_IN_AS_PREFIX = "You'll sign in as ";
export const USERNAME_HELPER_SIGN_IN_AS_SUFFIX = " — type this next time.";

/** The value the helper line echoes — the server's own normalization
 * (`normalize_username`: trim + lowercase), so what's shown is exactly
 * what will be stored and typed next time. */
export function usernamePreview(raw: string): string {
  return raw.trim().toLowerCase();
}

// Client-side ONLY a pre-fill suggestion — the field stays editable and the
// server is the sole source of truth for the real normalization/uniqueness
// rule (`app/repositories/auth_repository.py::normalize_username` +
// `USERNAME_RE = ^[a-z0-9._-]{3,30}$`). ADR-002 A4 decision 1: "pre-filled
// with a slugified suggestion from the first name (e.g. display name
// 'Worker One' -> suggested worker) so a teen just taps Continue." Best-effort
// only — an unusual name that slugifies to something invalid/too-short
// still leaves an EDITABLE field, never a blocked one.
export function suggestUsername(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return first
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "");
}

export const PASSWORD_FIELD_LABEL_CHOOSE = "Choose a password";

const PASSWORD_MIN_LENGTH = 8;

export function passwordCounterText(length: number): string {
  return length >= PASSWORD_MIN_LENGTH
    ? "At least 8 characters ✓"
    : `At least 8 characters (${length} so far)`;
}

export function passwordMeetsMinLength(length: number): boolean {
  return length >= PASSWORD_MIN_LENGTH;
}

export const PASSWORD_TOO_SHORT_ERROR = "A little longer — at least 8 characters.";
export const ONBOARDING_SAVE_FAILURE_ERROR =
  "Couldn't save your password — check your connection and try again.";
export const ONBOARDING_PRIMARY_BUTTON = "Set my password";

// --- Not in the UX copy table (§1.4 covers only the network-failure row) —
// written defensively, in the app's existing voice, to cover the
// server's 422 (malformed username) / 409 (username taken) responses so
// neither ever falls through to a raw error string. Flagged in the PR for
// UX Researcher awareness, non-blocking. ---
export const USERNAME_INVALID_ERROR =
  "That name needs to be letters, numbers, dots, dashes, or underscores — no spaces.";
export const USERNAME_TAKEN_ERROR = "That name's already taken — try a different one.";
export const GENERIC_FALLBACK_ERROR = "Something went wrong — try again.";

// ---------------------------------------------------------------------------
// Password show/hide toggle (shared by every password field in this
// sprint's scope — UX §1.2/§2.1: default ON, always present, ≥44px target).
// ---------------------------------------------------------------------------

export const SHOW_PASSWORD_LABEL = "Show password";
export const HIDE_PASSWORD_LABEL = "Hide password";

// ---------------------------------------------------------------------------
// Admin — recovery. `giveLinkHint()` LIVED HERE and is SUPERSEDED, per UX
// §6.1 Ruling 1: its job was to reframe a standalone button, and inside a
// chooser whose whole subject is already "can't get in," the words "If
// <first> can't get in" are redundant. Its replacement is
// `recoveryOptionLinkSub()` below. It was removed in the same pass that
// added the chooser so no governed string survives with no render site —
// that removal is required by the ruling, not incidental cleanup.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Sprint-007 D6 — passkeys (WebAuthn, ADR-002 Amendment 4 decision 3). All
// literal strings below are copied VERBATIM from `specs/ux-auth-v2-flows.md`
// §6's copy table (same governing rule as the block above) — NEVER "passkey"/
// "WebAuthn"/"biometric authentication" in any of these; "Face ID or your
// fingerprint" (first-run/everywhere a person sees it) or "Quick sign-in"
// (Settings section heading only, a plain umbrella term) per UX §7's
// copy-dictionary amendment.
// ---------------------------------------------------------------------------

// --- Post-onboarding opt-in step (UX §1.3) — same screen reused verbatim
// for the Settings "Add a quick sign-in" re-offer (UX §4.2). ---

export const BIOMETRIC_STEP_HEADING = "Want a faster way in next time?";
export const BIOMETRIC_STEP_BODY =
  "Use Face ID or your fingerprint to get into Hayloft without typing your password.";
export const BIOMETRIC_STEP_ACCEPT = "Turn it on";
export const BIOMETRIC_STEP_DECLINE = "Not now";
export const BIOMETRIC_STEP_FOOTER = "You can turn this on any time from Your sign-in, under More.";
export const BIOMETRIC_SUCCESS =
  "You're all set — next time, just use Face ID or your fingerprint.";
export const BIOMETRIC_SOFT_FAIL =
  "No problem — you can turn this on later from Your sign-in, under More.";

// --- Returning login one-tap button (UX §2.1/§2.2) ---
// UX review 2026-10: the platform-guessed "Use Face ID" / "Use your
// fingerprint" label (`returningLoginBiometricLabel`, retired) misnamed
// Touch ID Macs and Windows Hello; the button now always reads
// `RETURNING_LOGIN_PASSKEY_BUTTON` ("Use Face ID or fingerprint", above).

// --- Token-invalid screen (UX §3.4, ADR-002 A4-R6) ---

export const TOKEN_INVALID_HEADING = "This link isn't working anymore";
export const TOKEN_INVALID_BODY = "Ask your farm admin for a new one.";

// --- "Your sign-in" settings surface (UX §4) ---

export const SIGN_IN_SETTINGS_ROW_TITLE = "Your sign-in";
export const SIGN_IN_SETTINGS_ROW_SUB =
  "Change your password, or set up Face ID or your fingerprint.";
export const SIGN_IN_SETTINGS_HEADING = "Your sign-in";
export const SETTINGS_PASSWORD_ACTION = "Change your password";
export const SETTINGS_CURRENT_PASSWORD_LABEL = "Current password";
export const SETTINGS_SAVE_NEW_PASSWORD_BUTTON = "Save new password";
export const SETTINGS_PASSWORD_CHANGED_SUCCESS = "Your password is changed.";
export const SETTINGS_CURRENT_PASSWORD_MISMATCH_ERROR = "That didn't match — try again.";

export const SETTINGS_PASSKEY_SECTION_HEADING = "Quick sign-in (Face ID or fingerprint)";
export const SETTINGS_PASSKEY_EMPTY_STATE =
  "You're not using Face ID or a fingerprint to get in yet.";
export const SETTINGS_ADD_PASSKEY_ROW = "+ Add a quick sign-in";
export const SETTINGS_REMOVE_PASSKEY_CONFIRM_TITLE = "Remove this quick sign-in?";
export const SETTINGS_REMOVE_PASSKEY_CONFIRM_BODY =
  "You'll need your password to get in from this device next time.";
export const SETTINGS_REMOVE_PASSKEY_CONFIRM_CONFIRM = "Remove it";
export const SETTINGS_REMOVE_PASSKEY_CONFIRM_CANCEL = "Cancel";

// --- Sign out (UX review 2026-10, fix/ux-signin) — NOT yet in
// `specs/ux-auth-v2-flows.md` §6; written from the review brief and flagged
// for the spec. Replaces the app-bar's instant "Done for now" button, which
// read as "I'm finished with my chores" and silently logged people out. It
// now lives on More / Your sign-in (and the desktop sidebar footer) and
// always asks first. ---

export const SIGN_OUT_ACTION = "Sign out";
export function signOutConfirmTitle(isDesktop: boolean): string {
  return isDesktop ? "Sign out of Hayloft on this computer?" : "Sign out of Hayloft on this phone?";
}
export const SIGN_OUT_CONFIRM_BODY =
  "You'll need your sign-in name and password, or Face ID or your fingerprint, to get back in.";
/** Shown in the sign-out confirm when taps are still waiting to send —
 * signing out clears them from this phone. */
export function signOutUnsentWarning(count: number): string {
  const taps = count === 1 ? "1 tap on this phone hasn't" : `${count} taps on this phone haven't`;
  return `${taps} been sent yet. Signing out now will lose ${count === 1 ? "it" : "them"} — wait until you have signal.`;
}
export const SIGN_OUT_CONFIRM_CONFIRM = "Sign out";
export const SIGN_OUT_CONFIRM_CANCEL = "Cancel";

/** UX review 2026-10 — the admin's OWN person card. It used to say "This
 * is for other people…" (`SELF_RECOVERY_BLOCK_HINT`, below, unchanged)
 * and then offer "Change your password" and "Stop this link from working"
 * right under it. Now the hint is followed only by this pointer to Your
 * sign-in — a navigation, not a self-action. */
export const SELF_CARD_SIGN_IN_POINTER = "Go to Your sign-in";

// ===========================================================================
// TD-058 — admin-set password + forced change on next login.
//
// EVERY literal string from here to the end of this file is copied VERBATIM
// from `specs/ux-auth-v2-flows.md` §6.1's table (UX Researcher, 2026-08-06),
// governed by ADR-002 Amendment 12 §2.8 ("`web/src/lib/authCopy.ts` is a
// governed file — UX Researcher writes these strings into
// `specs/ux-auth-v2-flows.md` §6 first; developers must not author them").
// Do not reword, shorten, or "improve" one of these without changing §6.1
// first. Strings §6.1 marks "unchanged, reused verbatim" are NOT re-declared
// here — they are the existing constants above, reused by reference, which
// is the point of that instruction.
//
// Password rules are the SERVER's, not this file's: `app/security/
// passwords.py` — min 8, max 256, NIST SP 800-63B length-only, no
// composition rules, all Unicode and spaces accepted.
// ===========================================================================

// --- §6.1 A — Person Detail recovery section (admin-facing) ----------------

/** Replaces the literal "Their link" heading. UX §6.1 Ruling 1: the section
 * is no longer about a mechanism, it is about the outcome. */
export const RECOVERY_SECTION_HEADING = "Getting back in";

/**
 * Shown IN ADDITION to `linkStatusLabel()` while the person's
 * `password_reset_required` flag is true — so a never-completed forced
 * change is visible to the admin rather than silent (UX §6.1 Ruling 4:
 * "the pending-state line on the person card so a never-completed change is
 * visible rather than silent").
 *
 * ### NOT YET RENDERED — blocked on a backend field, DEFERRED as TD-061
 *
 * `GET /users` (`app/schemas/auth.py::UserAuthStatusOut`) does not carry
 * `password_reset_required`, so `PersonDetailOverlay` has no honest way to
 * know when to show this. Deriving it from "we just set a password in this
 * session" would look right and then vanish on reload — which is precisely
 * the silence the ruling exists to remove, so it is worse than not showing
 * it. The SA reviewed and upheld that (Amendment 12 §11.2): the field is
 * deferred, this string ships with no render site, and the deferral is
 * logged as **TD-061** (bundled with `has_credentials`, which is the exact
 * gate `PersonDetailOverlay.canSetPassword` proxies today). Wiring it is
 * one render site and no copy change.
 *
 * This is deliberately NOT the `giveLinkHint` case: that string was
 * SUPERSEDED (stale, no render site will ever exist again) and had to go.
 * This one is authored, correct, and pending one additive boolean on an
 * admin-only response. Dead-but-correct is tolerable while it is LOGGED;
 * unlogged is exactly how it would decay into the `giveLinkHint` condition.
 */
export function passwordChangePendingStatus(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `${first} has to pick their own password next time they get in.`;
}

/** The ONE recovery action. UX §6.1 Ruling 1 collapsed "Give <first> a new
 * link" and "Set a password" into this, because Emily does not think in
 * mechanisms — she thinks "Colby can't get in and I'm standing in the barn."
 * The mechanism question moves into the chooser this button opens. */
export function getBackInButtonLabel(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Get ${first} back in`;
}

export function recoveryChooserTitle(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `How is ${first} getting back in?`;
}

/** LISTED FIRST AND STYLED PRIMARY — deliberate, and the one place UX is
 * allowed to carry ADR-002 A4's preference without overriding the client:
 * the safer path stays the path of least resistance, the other path stays
 * one tap away, and NEITHER is nagged about. No warning icon on the other
 * option, no "are you sure you don't want the link" interstitial. */
export const RECOVERY_OPTION_LINK_LABEL = "Send them a link";

/** Supersedes the removed `giveLinkHint()`. */
export function recoveryOptionLinkSub(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Text it to ${first} or show them the code. Nothing for them to type.`;
}

export const RECOVERY_OPTION_PASSWORD_LABEL = "Give them a password";

export function recoveryOptionPasswordSub(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Best if ${first} isn't here or has no signal. You'll read it to them, and they'll pick their own the moment they get in.`;
}

/** The DISABLED state's sub-line. A person with no `user_credentials` row
 * also has no `users.username`, so there is nothing for them to type into
 * the "Sign-in name" field — handing the admin a password for someone who
 * cannot reach a login form is a dead end she would only discover on the
 * phone with a teenager. The server agrees: `POST /users/{id}/password`
 * answers 409 for that target (Amendment 12 §3 C1). */
export function recoveryOptionPasswordUnavailableSub(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `${first} hasn't finished setting up Hayloft yet. Send a link instead.`;
}

export const RECOVERY_CHOOSER_CANCEL = "Never mind";

// --- §6.1 A — the set-password form ---------------------------------------

export function setPasswordHeading(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Set a password for ${first}`;
}

export function setPasswordFieldLabel(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `A password for ${first}`;
}

export function setPasswordHelperText(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Pick something you can say out loud. ${first} will choose their own as soon as they get in.`;
}

/** The server's `MAX_PASSWORD_LENGTH = 256` (a 422 with
 * `_PASSWORD_LENGTH_DETAIL`). Same length-only rule as the minimum — there
 * is no composition requirement to explain, by design (A4 decision 2). */
export const PASSWORD_TOO_LONG_ERROR = "That one's too long — keep it under 256 characters.";

export const SET_PASSWORD_SUBMIT_BUTTON = "Set this password";
export const SET_PASSWORD_CANCEL_BUTTON = "Cancel";

/** The suggestion generator's button. UX §6.1 Ruling 4 is emphatic that the
 * generated value must be WORD-BASED AND DICTATABLE (`barn-otter-nine-fence`),
 * never a random character string: a random string is unreadable over a
 * phone, so the admin retypes it wrong or abandons the feature and types
 * `Fiddlesticks1` instead — the exact outcome this button exists to prevent.
 * The generator itself is `lib/passphrase.ts::suggestPassphrase`.
 *
 * This is the only real mitigation available for accepted risk AR-2
 * (human-chosen entropy replacing 256-bit CSPRNG); copy cannot fix entropy. */
export const SET_PASSWORD_SUGGEST_BUTTON = "Make one up";

// --- §6.1 A — the confirm step --------------------------------------------

export function setPasswordConfirmTitle(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Set a new password for ${first}?`;
}

/** Names every consequence the single server transaction actually has —
 * password overwritten, links revoked, sessions revoked, flag set
 * (Amendment 12 §2.1). Nothing here is aspirational. */
export function setPasswordConfirmBody(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `${first}'s old password stops working. Any link they have stops working. They'll be signed out on every phone. Next time they get in, they'll have to pick their own password.`;
}

export const SET_PASSWORD_CONFIRM_LABEL = "Yes, set it";
export const SET_PASSWORD_CONFIRM_CANCEL_LABEL = "Cancel";

// --- §6.1 A2 — the one-time password card ---------------------------------
//
// UX §6.1 Ruling 2: NO PRINT BUTTON. `LinkCardOverlay` has "Print this card"
// and that is right for a link — a printed magic link is single-use,
// expiring, and self-destructing. A printed password is a durable credential
// sitting on a barn wall. Copy only.

export function passwordCardTitle(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `${first}'s password`;
}

export function passwordCardSignInLine(displayName: string, username: string): string {
  const first = firstName(displayName) || displayName;
  return `${first} types ${username}, then this password.`;
}

export function passwordCardInstructions(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Read this to ${first}, or text it. They'll pick their own password the moment they get in.`;
}

export function passwordCardOnceHint(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `This won't be shown again, and Hayloft doesn't keep a copy. Once ${first} is back in, delete anything you sent them.`;
}

export const PASSWORD_CARD_COPY_BUTTON = "Copy it";
export const PASSWORD_CARD_COPIED_LABEL = "Copied!";

export const PASSWORD_CARD_CLOSE_CONFIRM_TITLE = "This password won't be shown again";

export function passwordCardCloseConfirmBody(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `Make sure ${first} has it before you go. If it gets lost, you can set another one.`;
}

/** Inverted, matching `LinkCardOverlay`: the CONFIRM button is the safe
 * "stay here" action and the CANCEL button is the one that closes. */
export const PASSWORD_CARD_CLOSE_CONFIRM_CONFIRM = "Go back";
export const PASSWORD_CARD_CLOSE_CONFIRM_CANCEL = "Close anyway";

// --- §6.1 A3 — self-target block ------------------------------------------

/** Shown IN PLACE OF the recovery action on the admin's own card. The server
 * refuses a self-target with a 409 (Amendment 12 §2.4) because
 * `revoke_all_user_credentials` has no `keep_session_id` — a self-target
 * would kill her own session mid-flight while replacing her own password
 * with a value she typed once, unverified, on a phone, in a barn. The UI
 * must not offer the action at all rather than let her discover that. */
export const SELF_RECOVERY_BLOCK_HINT =
  "This is for other people. To change your own password, open More, then Your sign-in.";

// --- §6.1 A4 — result states ----------------------------------------------

/** `role="status"` — a success, never an alert. */
export function setPasswordSuccessMessage(displayName: string): string {
  const first = firstName(displayName) || displayName;
  return `${first}'s password is set. They'll pick their own next time they get in.`;
}

/** `role="alert"` — this one IS a genuine failure shown to the admin, so
 * assertive styling is correct here (unlike `RESET_REQUIRED_REFUSED_MESSAGE`
 * below, which is shown to the HELPER and must never read as an error). */
export const SET_PASSWORD_GENERIC_ERROR = "Couldn't set that password — try again.";

// --- §6.1 B — helper side, the forced change ------------------------------

/** First entry into the forced-change screen — literally "the password you
 * JUST used." */
export const MUST_CHANGE_BODY =
  "The password you just used came from your farm admin. Pick your own now, so only you know it.";

/** UX §6.1 B3 — replaces `MUST_CHANGE_BODY` on any load after the first:
 * "you're STILL using" rather than "you just used." Reached when the person
 * abandoned the change and came back, which the client observes as a
 * reset-scope refusal arriving while they are already inside the app
 * (see `RESET_REQUIRED_BLOCK_*` below) rather than as a fresh login. */
export const MUST_CHANGE_RETURN_BODY =
  "You're still using the password your farm admin gave you. Pick your own to finish.";

/** The existing `ONBOARDING_NOT_YOU_LINE` says "ask for the right link" —
 * there is NO link in this flow, so it cannot be reused. */
export const MUST_CHANGE_NOT_YOU_LINE = "Not you? Stop, and ask your farm admin.";

/** `role="status"`, then straight to the existing Face ID / fingerprint
 * step. */
export const MUST_CHANGE_SUCCESS = "Done — that one's yours now.";

// --- §6.1 B2 — the server refusal (new user-visible state) -----------------
//
// Until TD-049 the forced change was a CLIENT-SIDE route guard, so a
// reset-required person could never SEE a refusal — the app simply never
// rendered anything else. With the real server-side gate, every route
// outside the three-entry allowlist answers 403 while the flag is set. That
// refusal must not read as an error, a punishment, or a bug.
//
// IT IS A QUEUE, NOT A WALL.

export const RESET_REQUIRED_BLOCK_HEADING = "One thing first";
export const RESET_REQUIRED_BLOCK_BODY =
  "Pick your own password and the rest of Hayloft opens up. Takes a second.";
export const RESET_REQUIRED_BLOCK_BUTTON = "Pick my password";

/**
 * THE STYLING OF THIS STRING IS LOAD-BEARING, NOT COSMETIC.
 *
 * UX §6.1 B2 pins it: `role="status" aria-live="polite"`, NEVER
 * `role="alert"`, and NEVER styled with `.error-hint` / `is-destructive`.
 * "An assertive red alert would teach a teenager that they broke something.
 * They didn't."
 *
 * Its render site uses `.auth-queue-hint` — a calm, non-destructive class
 * added for exactly this purpose. Do not restyle it and do not swap the
 * role.
 */
export const RESET_REQUIRED_REFUSED_MESSAGE =
  "That'll be here once you've picked your own password. Let's do that first.";
